import { useEffect, useMemo, useState } from 'react';
import { Inbox, Search, SquarePen, Archive, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUserData } from '@/lib/auth';
import { useWhatsAppConfigs } from '@/lib/hooks/useWhatsAppConfigs';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { arrangeChats, type ChatLike } from '@/lib/services/whatsapp/chatListRules';
import { etichettaNumero } from '@/lib/services/whatsapp/phoneDigits';
import { NewChatModal } from '@/components/whatsapp2/sidebar/NewChatModal';
import { useChatRowPrefs } from '@/components/whatsapp2/sidebar/useChatRowPrefs';
import { tip } from './ChatTooltip';
import { AllInboxRow } from './AllInboxRow';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useMetaMessagingStore, type MetaConversation } from '@/lib/stores/metaMessagingStore';
import { useTeamChatStore } from '@/lib/stores/teamChatStore';
import { useAllConversations } from '@/lib/hooks/useAllConversations';
import { useMetaChannels } from '@/lib/hooks/useMetaChannels';
import type { UnifiedChatItem, WhatsAppChatLike } from '@/lib/utils/chatListMerging';
import { searchInboxItems } from '@/lib/utils/allInboxSearch';
import { useWhatsAppSearch } from '@/lib/hooks/whatsapp/useWhatsAppSearch';
import { ChatView } from '@/components/whatsapp2/chat';
import { MetaThread } from '@/components/meta-messaging/MetaThread';

interface AllInboxProps {
  compact?: boolean;
  includeTeam: boolean;
  includeWhatsApp: boolean;
  /** WhatsApp pronto (sessione collegata): senza, le chat WhatsApp non si aprono qui. */
  whatsappReady: boolean;
}

/** Chiave in whatsapp_chat_meta: il JID per WhatsApp, 'meta:<id>' per Instagram/Messenger, nessuna per il team. */
function prefsKey(item: UnifiedChatItem): string | null {
  if (item.source === 'whatsapp') return item.id;
  if (item.source === 'meta') return `meta:${item.id}`;
  return null;
}

/** Ultima attivita' per capire se una conversazione archiviata ha ricevuto un messaggio dopo. */
function asChatLike(item: UnifiedChatItem): ChatLike {
  if (item.source === 'whatsapp') {
    const raw = item.raw as { lastMessage?: { timestamp: number; fromMe: boolean } };
    return { id: item.id, lastMessage: raw?.lastMessage ?? null };
  }
  const inbound = (item.raw as { last_inbound_at?: string | null })?.last_inbound_at;
  const ts = inbound ? new Date(inbound).getTime() : 0;
  return { id: item.id, lastMessage: ts ? { timestamp: ts, fromMe: false } : null };
}

/**
 * Vista "Tutte" del drawer Messaggi: lista unificata di WhatsApp, Instagram,
 * Messenger e chat di team, ordinata per ultima attivita', con l'etichetta di
 * provenienza su ogni riga. A destra il thread del canale scelto: WhatsApp e
 * Meta si aprono qui; una chat di team porta alla scheda Team (il suo pannello
 * ha lista e thread insieme).
 */
export function AllInbox({ compact = false, includeTeam, includeWhatsApp, whatsappReady }: AllInboxProps) {
  const [query, setQuery] = useState('');
  const { items } = useAllConversations({ includeTeam, includeWhatsApp });
  // Stessa ricerca della scheda WhatsApp (numero, chat non ancora caricate, nome del Lead/Cliente)
  const { results: whatsappHits } = useWhatsAppSearch(includeWhatsApp && whatsappReady ? query : '');
  const selection = useWhatsAppDrawerStore((s) => s.allSelection);
  const setAllSelection = useWhatsAppDrawerStore((s) => s.setAllSelection);
  const openTeamChat = useWhatsAppDrawerStore((s) => s.openTeamChat);
  const selectWhatsAppChat = useWhatsAppChatStore((s) => s.selectChat);
  const addChatToStore = useWhatsAppChatStore((s) => s.addChatToStore);
  const selectMeta = useMetaMessagingStore((s) => s.selectConversation);
  const metaConversations = useMetaMessagingStore((s) => s.conversations);
  const selectTeamChat = useTeamChatStore((s) => s.selectChat);
  const whatsappHasMore = useWhatsAppChatStore((s) => s.chatsHasMore);
  const whatsappLoadingMore = useWhatsAppChatStore((s) => s.chatsLoadingMore);
  const loadMoreWhatsApp = useWhatsAppChatStore((s) => s.loadMoreChats);
  const { canSend } = useMetaChannels();
  const { organizationId } = useUserData();
  const { configs } = useWhatsAppConfigs();
  const whatsappLoading = useWhatsAppChatStore((s) => s.chatsLoading);
  const metaLoading = useMetaMessagingStore((s) => s.loading);
  const chatMeta = useWhatsAppChatMetaStore((s) => s.chatMeta);
  const fetchChatMeta = useWhatsAppChatMetaStore((s) => s.fetchChatMeta);
  const rowPrefs = useChatRowPrefs(organizationId);
  const [showArchived, setShowArchived] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);

  // Fissa/archivia/silenzia: legge le righe di whatsapp_chat_meta delle conversazioni in lista.
  const keysSignature = useMemo(() => items.map(prefsKey).filter(Boolean).join('|'), [items]);
  useEffect(() => {
    if (!organizationId || !keysSignature) return;
    void fetchChatMeta(keysSignature.split('|'), organizationId);
  }, [keysSignature, organizationId, fetchChatMeta]);

  const { rows, archivedCount } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q ? searchInboxItems(items, query, whatsappHits as WhatsAppChatLike[]) : items;
    const wrapped = matching.map((item) => ({ ...asChatLike(item), id: `${item.source}:${item.id}`, item }));
    const byId = new Map(wrapped.map((w) => [w.id, w.item]));
    const { visible, archived } = arrangeChats(wrapped, (id) => {
      const it = byId.get(id);
      const key = it ? prefsKey(it) : null;
      return key ? chatMeta.get(key) : null;
    });
    const list = q ? [...visible, ...archived] : showArchived ? archived : visible;
    return { rows: list.map((w) => w.item), archivedCount: archived.length };
  }, [items, query, whatsappHits, chatMeta, showArchived]);

  useEffect(() => {
    if (showArchived && archivedCount === 0) setShowArchived(false);
  }, [showArchived, archivedCount]);

  const loadingList = items.length === 0 && ((includeWhatsApp && whatsappLoading) || metaLoading);
  const waitingWhatsApp = items.length === 0 && includeWhatsApp && !whatsappReady;

  const handleSelect = (item: UnifiedChatItem) => {
    if (item.source === 'team') {
      void selectTeamChat(item.id);
      openTeamChat(item.id);
      return;
    }
    if (item.source === 'whatsapp' && !whatsappReady) {
      // Prima il clic non faceva nulla: si dice perche'.
      let activeId: string | null = null;
      try { activeId = localStorage.getItem('whatsapp_active_config'); } catch { /* storage non disponibile */ }
      const cfg = configs.find((c) => c.id === activeId) || (configs.length === 1 ? configs[0] : null);
      const nome = cfg ? (cfg.displayName || etichettaNumero(cfg.phoneNumber)) : '';
      toast(`Il numero WhatsApp${nome ? ` ${nome}` : ''} si sta collegando: riprova tra poco. Se non si collega, apri la scheda WhatsApp per vedere cosa manca.`);
      return;
    }
    if (item.source === 'whatsapp') {
      addChatToStore(item.raw as Parameters<typeof addChatToStore>[0]);
      selectWhatsAppChat(item.id);
    } else {
      selectMeta(item.id);
    }
    setAllSelection({ source: item.source, id: item.id });
  };

  const selectedMeta: MetaConversation | null =
    selection?.source === 'meta' ? metaConversations.find((c) => c.id === selection.id) ?? null : null;
  const hasSelection = Boolean(selection && (selection.source === 'meta' ? selectedMeta : whatsappReady));

  return (
    <div className="flex-1 flex overflow-hidden bg-[#FBFBFA] dark:bg-[#191919]">
      <div
        className={`${compact ? 'w-[370px]' : 'w-[400px]'} h-full flex-shrink-0 ${hasSelection ? 'hidden lg:flex' : 'flex'} flex-col overflow-hidden border-r border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#191919]`}
      >
        <div className="px-3 pt-3 pb-2 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Inbox className="h-4 w-4 text-[#787774]" />
            <h3 className="text-sm font-semibold text-[#37352F] dark:text-white">Tutte le conversazioni</h3>
            <span className="text-xs text-[#a4a4a0]">{items.length}</span>
            {includeWhatsApp && configs.length > 0 && (
              <button
                type="button"
                onClick={() => setNewChatOpen(true)}
                aria-label="Nuova chat WhatsApp"
                {...tip('Nuova chat WhatsApp: cerca un Lead o Cliente, oppure scrivi un numero')}
                className="ml-auto p-1.5 rounded text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10"
              >
                <SquarePen className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a4a4a0]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca in tutti i canali"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-transparent focus:border-[#2383e2] focus:outline-none text-[#37352F] dark:text-white placeholder:text-[#a4a4a0]"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!query && (showArchived || archivedCount > 0) && (
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#37352F] dark:text-white border-b border-[rgba(55,53,47,0.06)] dark:border-white/5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5"
            >
              {showArchived ? <ArrowLeft className="h-4 w-4 text-[#787774]" /> : <Archive className="h-4 w-4 text-[#787774]" />}
              <span className="flex-1 text-left">{showArchived ? 'Torna a tutte le conversazioni' : 'Archiviate'}</span>
              {!showArchived && <span className="text-xs text-[#787774]">{archivedCount}</span>}
            </button>
          )}
          {rows.length === 0 && (
            <div className="px-4 py-10 text-center">
              {loadingList && !query ? (
                <>
                  <Loader2 className="h-6 w-6 text-[#c9c9c5] mx-auto mb-2 animate-spin" />
                  <p className="text-sm text-[#787774]">Carico le conversazioni...</p>
                </>
              ) : (
                <>
                  <Inbox className="h-8 w-8 text-[#c9c9c5] mx-auto mb-2" />
                  <p className="text-sm text-[#787774]">
                    {query ? 'Nessuna conversazione trovata'
                      : showArchived ? 'Nessuna conversazione archiviata'
                      : waitingWhatsApp ? 'WhatsApp non e\u0300 ancora collegato: le chat compariranno qui appena pronto. Controlla la scheda WhatsApp se non succede.'
                      : 'Nessuna conversazione ancora'}
                  </p>
                </>
              )}
            </div>
          )}
          {rows.map((item) => {
            const key = prefsKey(item);
            const chat = asChatLike(item);
            return (
              <AllInboxRow
                key={`${item.source}:${item.id}`}
                item={item}
                active={selection?.source === item.source && selection?.id === item.id}
                whatsappReady={whatsappReady}
                onSelect={handleSelect}
                prefs={key ? rowPrefs.stateOf(key, chat) : null}
                prefsAvailable={rowPrefs.prefsAvailable}
                onTogglePin={() => key && rowPrefs.togglePin(key, chat)}
                onToggleArchive={() => key && rowPrefs.toggleArchive(key, chat)}
                onMute={(opt) => key && rowPrefs.mute(key, opt)}
              />
            );
          })}
          {includeWhatsApp && whatsappReady && whatsappHasMore && !query && (
            <button
              type="button"
              onClick={() => void loadMoreWhatsApp()}
              disabled={whatsappLoadingMore}
              className="w-full py-2.5 text-xs font-medium text-[#2383e2] hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 disabled:opacity-50"
            >
              {whatsappLoadingMore ? 'Caricamento...' : 'Carica altre chat WhatsApp'}
            </button>
          )}
        </div>
      </div>

      <div className={`flex-1 h-full min-w-0 overflow-hidden ${hasSelection ? 'flex' : 'hidden lg:flex'} flex-col`}>
        {selection?.source === 'meta' && selectedMeta ? (
          <MetaThread conversation={selectedMeta} canSend={canSend} compact={compact} onBack={() => setAllSelection(null)} />
        ) : selection?.source === 'whatsapp' && whatsappReady ? (
          <ChatView compactMode={compact} onBack={() => setAllSelection(null)} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Inbox className="h-10 w-10 text-[#c9c9c5] mx-auto mb-3" />
              <p className="text-sm text-[#787774]">Scegli una conversazione da qualsiasi canale</p>
            </div>
          </div>
        )}
      </div>

      {newChatOpen && <NewChatModal onClose={() => setNewChatOpen(false)} />}
    </div>
  );
}
